import { NgIf, TitleCasePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { SafeResourceUrlPipe } from "src/app/pipes/safeResourceUrl/safe-resource-url.pipe";
import { ImageService } from "src/app/servicios/image/image.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { FullImageViewerComponent } from "../../full-image-viewer/full-image-viewer.component";
import { SkeletonImageComponent } from "../../skeleton-image/skeleton-image/skeleton-image.component";
import { SliderComponent } from "../../slider/slider.component";
import { VideoNoticiaComponent } from "../video-noticia/video-noticia.component";

@Component({
  selector: "app-tipo-noticia-generico",
  imports: [FullImageViewerComponent, VideoNoticiaComponent, TitleCasePipe, SafeResourceUrlPipe, SkeletonImageComponent, SliderComponent, NgIf],
  providers: [ImageService],
  templateUrl: "./tipo-noticia-generico.component.html",
  styleUrl: "./tipo-noticia-generico.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Componente que se encarga de mostrar la cabecera de una noticia dependiendo del tipo de noticia.
 */
export class TipoNoticiaGenericoComponent implements OnChanges {
  // Inputs requeridos y opcionales
  @Input({ required: true }) type;
  @Input() noticia;
  @Input() imagenes_relacionadas;
  @Input() innerHeader: boolean = false;

  // Variables internas del componente
  currentIndex: number = 0;
  showFlag: boolean = false;
  showTitle: boolean = true;
  imagesSlider;
  customVideo;
  limiteVideo = false;
  playerVars;
  videoYoutube;
  videoPausado = true;
  playerYT;
  isMobile = false;
  showDivVideoArriba = false;

  // Referencias a elementos del DOM
  @ViewChild("tituloNoticia") tituloNoticia: ElementRef<HTMLDivElement>;
  @ViewChild("entradillaNoticia") entradillaNoticia: ElementRef<HTMLDivElement>;
  @ViewChild("videoComponent") videoComponent: any;

  constructor(
    private deviceService: DeviceDetectorService,
    private imageService: ImageService,
    private platformService: PlatformService,
  ) {}

  /**
   * ngOnInit:
   * - Configura las imágenes relacionadas en el servicio de imágenes.
   * - Detecta si el dispositivo es móvil.
   * - Configura la posición del video en función de la propiedad "posicion_video".
   */
  ngOnInit(): void {
    if (this.imagenes_relacionadas && this.imagenes_relacionadas) {
      this.imageService.setImagenes(this.imagenes_relacionadas);
    }
    this.isMobile = this.deviceService.isMobile();
    if (this.isMobile && this.noticia.posicion_video == 2) {
      this.showDivVideoArriba = true;
    }
  }

  /**
   * ngOnChanges:
   * - Actualiza las imágenes en el servicio cuando cambian las propiedades de imagenes_relacionadas.
   * - Verifica y actualiza la visibilidad del título en función del tipo de noticia.
   * @param changes Cambios en las propiedades de entrada.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.imagenes_relacionadas && this.imagenes_relacionadas) {
      this.imageService.setImagenes(this.imagenes_relacionadas);
    }
    if (changes.type) {
      this.checkShowTitle();
    }
  }

  /**
   * Desplaza la vista hasta un elemento con el ID indicado y reproduce el video.
   * @param id ID del elemento al que desplazarse.
   * @param url URL del video a reproducir.
   */
  scrollToDiv(id, url): void {
    if (this.platformService.isBrowser()) {
      const el = document.getElementById(id);
      el.scrollIntoView({ behavior: "smooth" });
      this.reproducirVideo(url);
    }
  }

  /**
   * Reproduce el video dado el URL, utilizando el reproductor de YouTube o un elemento de video personalizado.
   * @param url URL del video.
   */
  reproducirVideo(url): void {
    if (this.platformService.isBrowser()) {
      this.videoPausado = false;
      if (this.esVideoYoutube(url)) {
        this.playerYT.playVideo();
      } else {
        this.customVideo = document.getElementById("customVideo") as HTMLVideoElement;
        this.customVideo.play();
      }
    }
  }

  /**
   * Verifica si el video corresponde a un video de YouTube.
   * @param url URL del video.
   * @returns true si es video de YouTube; false en caso contrario.
   */
  esVideoYoutube(url): boolean {
    const regExp = /youtube.com/gi;
    if (url?.search(regExp) === -1) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * Muestra el lightbox de imágenes para la posición indicada.
   * @param index Índice de la imagen a mostrar.
   */
  showLightbox(index): void {
    this.imageService.setSeleccionada(index);
    this.showFlag = true;
  }

  /**
   * Cierra el lightbox y actualiza el índice actual.
   */
  closeEventHandler(): void {
    this.currentIndex = this.imageService.getIndexActual();
    this.showFlag = false;
  }

  /**
   * Después de la inicialización de la vista, configura el estilo de los elementos de título y entradilla en móvil.
   */
  ngAfterViewInit(): void {
    this.imageService.setImagenes(this.imagenes_relacionadas);
    if (this.isMobile) {
      this.tituloNoticia.nativeElement.style.textAlign = this.noticia.alineacion_titulo;
      this.entradillaNoticia.nativeElement.style.textAlign = this.noticia.alineacion_entradilla;
    }
  }

  /**
   * Muestra u oculta el título de la noticia en función del tipo.
   */
  checkShowTitle(): void {
    // Tipos de noticias que no deben mostrar el título
    if ([3, 5, 6, 7, 8, 9, 10, 11].includes(Number(this.type))) {
      this.showTitle = false;
      return;
    }
    this.showTitle = true;
  }
}
